@if (isLoading) {
  <div class="loader-overlay" role="status" aria-live="polite">
    <div class="heart-wrapper" aria-hidden="true">
      <svg class="heart-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path
          class="heart-shape"
          d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 
                 2 6 4 4 6.5 4c1.74 0 3.41 1 4.04 2.36C11.17 5 
                 12.76 4 14.5 4 17 4 19 6 19 8.5c0 3.78-3.4 
                 6.86-8.55 11.54L12 21.35z"
        />
        <polyline class="ekg" points="3 12 7 12 9 8 11 15 13 6 15 12 21 12" fill="none" />
      </svg>
    </div>
    <p class="loader-text mt-3 fw-semibold text-dark">Updating password...</p>
  </div>
}

@if (!isLoading) {
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-md-6 col-lg-5">
        <div class="card booking-card">
          <div
            class="card-header booking-card__header text-center justify-content-center flex-column pb-3"
          >
            <div class="booking-card__eyebrow">Security Reset</div>
            <h2 class="sub-title mb-0 fs-3">Set New Password</h2>
          </div>

          <div class="card-body p-4">
            <!-- Prominent Error Alert Banner -->
            @if (errorMessage) {
              <div
                class="alert alert-danger d-flex align-items-start mb-4 p-3 rounded-3 shadow-sm border-danger border-start border-4"
                role="alert"
              >
                <i
                  class="bi bi-exclamation-octagon-fill fs-4 me-3 text-danger flex-shrink-0 mt-1"
                ></i>
                <div class="flex-grow-1">
                  <div class="fw-bold text-danger">Update Failed</div>
                  <div class="small text-dark mt-1">{{ errorMessage }}</div>
                </div>
                <button
                  type="button"
                  class="btn-close ms-2"
                  (click)="errorMessage = null"
                  aria-label="Close error message"
                ></button>
              </div>
            }

            <p class="text-muted small text-center mb-4">
              Enter a strong new password for your practice portal account.
            </p>

            <form [formGroup]="changePasswordForm" (ngSubmit)="submit()">
              <!-- Password -->
              <div class="mb-3">
                <label for="password" class="form-label">New Password</label>
                <div class="input-group">
                  <input
                    id="password"
                    [type]="hidePassword() ? 'password' : 'text'"
                    class="form-control"
                    formControlName="password"
                    autocomplete="new-password"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    (click)="toggle('password')"
                  >
                    <i
                      class="bi"
                      [class.bi-eye]="hidePassword()"
                      [class.bi-eye-slash]="!hidePassword()"
                    ></i>
                  </button>
                </div>

                @if (changePasswordForm.controls['password'].touched && changePasswordForm.controls['password'].errors?.['required']) {
                  <div class="text-danger small mt-1">Password is required.</div>
                }
                @if (changePasswordForm.controls['password'].dirty) {
                  <ul class="small mt-2 mb-0 ps-3 list-unstyled">
                    <li [class.text-success]="changePasswordForm.controls['password'].value?.length >= 8" [class.text-muted]="!(changePasswordForm.controls['password'].value?.length >= 8)">
                      <i class="bi" [class.bi-check-circle-fill]="changePasswordForm.controls['password'].value?.length >= 8" [class.bi-circle]="!(changePasswordForm.controls['password'].value?.length >= 8)"></i>
                      At least 8 characters
                    </li>
                    <li [class.text-success]="hasUppercase()" [class.text-muted]="!hasUppercase()">
                      <i class="bi" [class.bi-check-circle-fill]="hasUppercase()" [class.bi-circle]="!hasUppercase()"></i>
                      At least one uppercase letter
                    </li>
                    <li [class.text-success]="hasLowercase()" [class.text-muted]="!hasLowercase()">
                      <i class="bi" [class.bi-check-circle-fill]="hasLowercase()" [class.bi-circle]="!hasLowercase()"></i>
                      At least one lowercase letter
                    </li>
                    <li [class.text-success]="hasDigit()" [class.text-muted]="!hasDigit()">
                      <i class="bi" [class.bi-check-circle-fill]="hasDigit()" [class.bi-circle]="!hasDigit()"></i>
                      At least one digit
                    </li>
                    <li [class.text-success]="hasSpecial()" [class.text-muted]="!hasSpecial()">
                      <i class="bi" [class.bi-check-circle-fill]="hasSpecial()" [class.bi-circle]="!hasSpecial()"></i>
                      At least one special character (!&#64;#$%...)
                    </li>
                  </ul>
                }
              </div>

              <!-- Confirm password -->
              <div class="mb-4">
                <label for="confirmPassword" class="form-label">Confirm Password</label>
                <div class="input-group">
                  <input
                    id="confirmPassword"
                    [type]="hideConfirmPassword() ? 'password' : 'text'"
                    class="form-control"
                    formControlName="confirmPassword"
                    autocomplete="new-password"
                    placeholder="Repeat new password"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    (click)="toggle('confirmPassword')"
                  >
                    <i
                      class="bi"
                      [class.bi-eye]="hideConfirmPassword()"
                      [class.bi-eye-slash]="!hideConfirmPassword()"
                    ></i>
                  </button>
                </div>

                @if (
                  changePasswordForm.errors?.['passwordMismatch'] &&
                  changePasswordForm.controls['confirmPassword'].touched
                ) {
                  <div class="text-danger small mt-1">Passwords do not match.</div>
                }
              </div>

              <!-- Submit -->
              <div class="d-grid mt-4">
                <button
                  type="submit"
                  class="btn btn-primary py-2"
                  [disabled]="changePasswordForm.invalid"
                >
                  <i class="bi bi-check2-circle me-1"></i> Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
}
